import React from 'react';
import { Stack, useBreakpoint } from '@moda/om';
import { CarouselCellFragment } from '../../generated/types/CarouselCellFragment';
import { useModuleTracking } from '../../hooks/useModuleTracking';
import { NormalizePumoStyles } from '../NormalizePumoStyles';
import { DividerModule, DividerModuleProps } from '../DividerPumoModule';
import { CAROUSEL_DEFAULT_CONFIG, SwiperCarousel } from '../Swiper';
import { CarouselCell, LazyGrid } from '../Grid';
import { ResponsiveImage } from '../ResponsiveImage';
import { ProductDetailPageFragment } from '../../generated/types/ProductDetailPageFragment';

import './RecommendationsCarousel.scss';

export const PENDING_SRC = '//:0';

interface Props {
  loading: boolean;
  identifier: string;
  error: Error | undefined;
  divider: DividerModuleProps;
  lookImageUrl?: string | null;
  cells: CarouselCellFragment[];
  variant?: ProductDetailPageFragment;
  howToWearItLargeDisplayFlag?: boolean | undefined;
}

export const RecommendationsCarousel: React.FC<Props> = ({
  loading,
  error,
  divider,
  cells,
  identifier,
  lookImageUrl,
  variant = { name: '' },
  howToWearItLargeDisplayFlag
}) => {
  const { ref, onClick } = useModuleTracking({ name: divider.text, attachClickHandler: false });
  const isMobile = useBreakpoint({ lt: 'md' }).matches;
  const direction: 'vertical' | 'horizontal' = isMobile ? 'vertical' : 'horizontal';

  if (loading || error || cells.length === 0) return null;

  return (
    <div ref={ref}>
      <NormalizePumoStyles className="RecommendationsCarousel">
        <DividerModule {...divider} />
      </NormalizePumoStyles>
      {howToWearItLargeDisplayFlag && lookImageUrl ? (
        <Stack direction={direction} space={2} className="RecommendationsCarousel__container">
          <ResponsiveImage
            className="RecommendationsCarousel__largeImage"
            src={lookImageUrl || PENDING_SRC}
            alt={variant.name || 'Product thumbnail'}
            maxWidth={425}
            maxHeight={700}
          />
          <LazyGrid className="RecommendationsCarousel__grid">
            {cells.map(carouselCell => (
              <CarouselCell
                key={carouselCell.__typename + carouselCell.id}
                carouselCell={carouselCell}
                moduleName={divider.text}
              />
            ))}
          </LazyGrid>
        </Stack>
      ) : (
        <SwiperCarousel
          identifier={identifier}
          {...CAROUSEL_DEFAULT_CONFIG}
          onClick={(swiper, event) => onClick(event)}
        >
          {cells.map(carouselCell => (
            <CarouselCell
              key={carouselCell.__typename + carouselCell.id}
              carouselCell={carouselCell}
              moduleName={divider.text}
            />
          ))}
        </SwiperCarousel>
      )}
    </div>
  );
};
